import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AlertService } from 'app/services/alert.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { QueryParams } from '../api.service';
import { WithAuditContext } from '../http-mutating-operations.interceptor';
import { ListResult, SemantriaAPIService } from '../semantria-api.service';

export interface ActionPlan {
  external_id: string;
  name?: string;
  notes: string;
  plan_id?: string;
  external_url?: string;
  status?: Status;
  metric_name: string;
  metric_original_value: number;
  metric_target_value: number;
  due_date: string;
  metadata?: {
    [key: string]: string;
  };
  assignee_user_id?: string;
  assignee_user_name?: string;
  assignee_user_email?: string;
  creator_user_id?: string;
  creator_user_name?: string;
  creator_user_email?: string;
  creation_date?: string;
  modification_date?: string;
  total_child_count?: number;
  new_child_count?: number;
  in_progress_child_count?: number;
  completed_child_count?: number;
  cancelled_child_count?: number;
  past_due_child_count?: number;
  action_items?: ActionItem[];
}

export interface ActionItem {
  external_id: string;
  name?: string;
  notes: string;
  item_id?: string;
  external_url?: string;
  status?: Status;
  metric_name?: string;
  metric_original_value?: string;
  metric_target_value?: string;
  due_date: string;
  metadata?: {
    [key: string]: string;
  };
  assignee_user_id?: string;
  assignee_user_name?: string;
  assignee_user_email?: string;
  item_type: string;
  item_value: string;
  creator_user_id?: string;
  creator_user_name?: string;
  creator_user_email?: string;
  creation_date?: string;
  modification_date?: string;
  total_child_count?: number;
  new_child_count?: number;
  in_progress_child_count?: number;
  completed_child_count?: number;
  cancelled_child_count?: number;
  past_due_child_count?: number;
  action_tasks?: ActionTask[];
}

export interface ActionTask {
  external_id: string;
  name?: string;
  notes: string;
  task_id?: string;
  external_url?: string;
  status?: Status;
  metric_name?: string;
  metric_original_value?: string;
  metric_target_value?: string;
  due_date: string;
  metadata?: {
    [key: string]: string;
  };
  assignee_user_id?: string;
  assignee_user_name?: string;
  assignee_user_email?: string;
  creator_user_id?: string;
  creator_user_name?: string;
  creator_user_email?: string;
  creation_date?: string;
  modification_date?: string;
}

export interface ActionPlanCreationFields {
  calculationId: string;
  collectionName: string;
  collectionDisplayName: string;
  driverName: string;
  driverColumn: string;
  breakdown: string;
  breakdownColumn?: string;
  notes: string;
  dueDate: string;
  originalMetric: number;
  targetMetric: number;
  projectId: string;
  projectName: string;
  dashboardId: string;
  scoringStrategy: string;
  filters?: string;
  comparisonFilters?: string;
  widgetGroupFilters?: string;
  weightField?: string;
}

export interface ActionItemCreationFields {
  name: string;
  dueDate: string;
  assignee: string;
  notes: string;
  itemType: string;
  itemValue: string;
}

export interface ActionTaskCreationFields {
  name: string;
  dueDate: string;
  assignee: string;
  notes: string;
}

export enum Status {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export const ACTION_PLAN_URL_PATH = '/actionplan';

@EnableClassTelemetry()
@Injectable()
export class ActionPlanService extends SemantriaAPIService {
  constructor(
    public http: HttpClient,
    public systemService: SystemService,
    public alertService: AlertService
  ) {
    super(http, systemService);
  }

  private formatActionPlansEndpoint(actionPlanId?: string): string {
    let endpoint = `${ACTION_PLAN_URL_PATH}/action-plans/`;

    if (actionPlanId) {
      endpoint += `${actionPlanId}/`;
    }

    return endpoint;
  }

  private formatActionItemsEndpoint(actionPlanId: string, actionItemId?: string): string {
    let endpoint = `${this.formatActionPlansEndpoint(actionPlanId)}items/`;

    if (actionItemId) {
      endpoint += `${actionItemId}/`;
    }

    return endpoint;
  }

  private formatActionTasksEndpoint(
    actionPlanId: string,
    actionItemId: string,
    actionTaskId?: string
  ): string {
    let endpoint = `${this.formatActionItemsEndpoint(actionPlanId, actionItemId)}tasks/`;

    if (actionTaskId) {
      endpoint += `${actionTaskId}/`;
    }

    return endpoint;
  }

  @WithAuditContext('action-plan-created', (_, args) => {
    return { groupId: args[0], external_id: args[1].external_id };
  })
  public createActionPlan(groupId: string, actionPlan: ActionPlan): Promise<ActionPlan> {
    return this.post<ActionPlan>(this.formatActionPlansEndpoint(), actionPlan, null, {
      group_id: groupId,
    });
  }

  public getActionPlan(
    groupId: string,
    actionPlanId: string,
    params?: QueryParams
  ): Promise<ActionPlan> {
    params = { ...(params ?? {}) };
    params['group_id'] = groupId;
    return this.get<ActionPlan>(this.formatActionPlansEndpoint(actionPlanId), params);
  }

  public listActionPlans(groupId: string, params?: QueryParams): Promise<ListResult<ActionPlan>> {
    params = { ...(params ?? {}) };
    params['group_id'] = groupId;
    return this.get<ListResult<ActionPlan>>(this.formatActionPlansEndpoint(), params);
  }

  @WithAuditContext('action-plan-updated', (_, args) => {
    return { groupId: args[0], external_id: args[1].external_id };
  })
  public updateActionPlan(groupId: string, actionPlan: ActionPlan): Promise<ActionPlan> {
    return this.put<ActionPlan>(
      `${this.formatActionPlansEndpoint(actionPlan.plan_id)}?group_id=${groupId}`,
      actionPlan
    );
  }

  @WithAuditContext('action-plan-deleted', (_, args) => {
    return { groupId: args[0], actionPlanId: args[1] };
  })
  public async deleteActionPlan(groupId: string, actionPlanId: string): Promise<void> {
    return this.delete<void>(`${this.formatActionPlansEndpoint(actionPlanId)}?group_id=${groupId}`);
  }

  @WithAuditContext('action-plan-item-created', (_, args) => {
    return { actionPlanId: args[0], actionItem: args[1].external_id };
  })
  public createActionItem(actionPlanId: string, actionItem: ActionItem): Promise<ActionItem> {
    return this.post<ActionItem>(this.formatActionItemsEndpoint(actionPlanId), actionItem);
  }

  public getActionItem(actionPlanId: string, actionItemId: string): Promise<ActionItem> {
    return this.get<ActionItem>(this.formatActionItemsEndpoint(actionPlanId, actionItemId));
  }

  public listActionItems(
    actionPlanId: string,
    params?: QueryParams
  ): Promise<ListResult<ActionItem>> {
    params = { ...(params ?? {}) };
    return this.get<ListResult<ActionItem>>(this.formatActionItemsEndpoint(actionPlanId), params);
  }

  @WithAuditContext('action-plan-item-updated', (_, args) => {
    return { actionPlanId: args[0], actionItem: args[1].external_id };
  })
  public updateActionItem(actionPlanId: string, actionItem: ActionItem): Promise<ActionItem> {
    return this.put<ActionItem>(
      this.formatActionItemsEndpoint(actionPlanId, actionItem.item_id),
      actionItem
    );
  }

  @WithAuditContext('action-plan-item-deleted', (_, args) => {
    return { actionPlanId: args[0], actionItemId: args[1] };
  })
  public async deleteActionItem(actionPlanId: string, actionItemId: string): Promise<void> {
    return this.delete<void>(this.formatActionItemsEndpoint(actionPlanId, actionItemId));
  }

  @WithAuditContext('action-plan-task-created', (_, args) => {
    return { actionPlanId: args[0], actionItemId: args[1], actionTask: args[2].external_id };
  })
  public createActionTask(
    actionPlanId: string,
    actionItemId: string,
    actionTask: ActionTask
  ): Promise<ActionTask> {
    return this.post<ActionTask>(
      this.formatActionTasksEndpoint(actionPlanId, actionItemId),
      actionTask
    );
  }

  public getActionTask(
    actionPlanId: string,
    actionItemId: string,
    actionTaskId: string
  ): Promise<ActionTask> {
    return this.get<ActionTask>(
      this.formatActionTasksEndpoint(actionPlanId, actionItemId, actionTaskId)
    );
  }

  public listActionTasks(
    actionPlanId: string,
    actionItemId: string,
    params?: QueryParams
  ): Promise<ListResult<ActionTask>> {
    params = { ...(params ?? {}) };
    return this.get<ListResult<ActionTask>>(
      this.formatActionTasksEndpoint(actionPlanId, actionItemId),
      params
    );
  }

  @WithAuditContext('action-plan-task-updated', (_, args) => {
    return { actionPlanId: args[0], actionItemId: args[1], actionTask: args[2].external_id };
  })
  public updateActionTask(
    actionPlanId: string,
    actionItemId: string,
    actionTask: ActionTask
  ): Promise<ActionTask> {
    return this.put<ActionTask>(
      this.formatActionTasksEndpoint(actionPlanId, actionItemId, actionTask.task_id),
      actionTask
    );
  }

  @WithAuditContext('action-plan-task-deleted', (_, args) => {
    return { actionPlanId: args[0], actionItemId: args[1], actionTaskId: args[2] };
  })
  public async deleteActionTask(
    actionPlanId: string,
    actionItemId: string,
    actionTaskId: string
  ): Promise<void> {
    return this.delete<void>(
      this.formatActionTasksEndpoint(actionPlanId, actionItemId, actionTaskId)
    );
  }

  public static createActionPlanObject(actionPlanFields: ActionPlanCreationFields): ActionPlan {
    return {
      external_id: `${actionPlanFields.collectionName} :: ${actionPlanFields.driverName} :: ${actionPlanFields.breakdown}`,
      notes: actionPlanFields.notes,
      due_date: actionPlanFields.dueDate,
      metric_name: actionPlanFields.driverName,
      metric_original_value: actionPlanFields.originalMetric,
      metric_target_value: actionPlanFields.targetMetric,
      metadata: {
        driverColumn: actionPlanFields.driverColumn,
        breakdown: actionPlanFields.breakdown,
        breakdownColumn: actionPlanFields.breakdownColumn,
        calculationId: actionPlanFields.calculationId,
        collectionId: actionPlanFields.collectionName,
        collectionDisplayName: actionPlanFields.collectionDisplayName,
        projectId: actionPlanFields.projectId,
        projectName: actionPlanFields.projectName,
        dashboardId: actionPlanFields.dashboardId,
        scoringStrategy: actionPlanFields.scoringStrategy,
        filters: actionPlanFields.filters,
        comparisonFilters: actionPlanFields.comparisonFilters,
        widgetGroupFilters: actionPlanFields.widgetGroupFilters,
        weightField: actionPlanFields.weightField,
      },
    };
  }

  public static createActionItemObject(actionItemFields: ActionItemCreationFields): ActionItem {
    return {
      external_id: `${actionItemFields.itemType} :: ${actionItemFields.itemValue}`,
      name: actionItemFields.name,
      due_date: actionItemFields.dueDate,
      notes: actionItemFields.notes,
      assignee_user_name: actionItemFields.assignee,
      item_type: actionItemFields.itemType,
      item_value: actionItemFields.itemValue,
      status: Status.NEW,
    };
  }

  public static createActionTaskObject(actionTaskFields: ActionTaskCreationFields): ActionTask {
    return {
      external_id: actionTaskFields.name,
      name: actionTaskFields.name,
      due_date: actionTaskFields.dueDate,
      notes: actionTaskFields.notes,
      assignee_user_name: actionTaskFields.assignee,
    };
  }
}
